/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import type {
    YomiriChapter,
    YomiriExtension,
    YomiriLanguageOption,
    YomiriManga,
    YomiriMangasPage,
    YomiriPage,
} from '@/yomiri/types/extension.ts';
import { yomiriDatabase } from '@/yomiri/storage/YomiriDatabase.ts';
import { fetchSourceResponse } from '@/yomiri/core/SourceHttp.ts';

const MANGADEX_API = 'https://api.mangadex.org';
const MANGADEX_COVERS = 'https://uploads.mangadex.org/covers';

function parseTitle(titleObj: Record<string, string> = {}): string {
    return (
        titleObj.es ||
        titleObj['es-la'] ||
        titleObj.en ||
        titleObj['ja-ro'] ||
        Object.values(titleObj)[0] ||
        'Título Desconocido'
    );
}

function parseDescription(descObj: Record<string, string> = {}): string {
    return descObj.es || descObj['es-la'] || descObj.en || Object.values(descObj)[0] || '';
}

function getCoverUrl(mangaId: string, relationships: any[] = []): string {
    const coverRel = relationships.find((rel) => rel.type === 'cover_art');
    const fileName = coverRel?.attributes?.fileName;
    if (fileName) {
        return `${MANGADEX_COVERS}/${mangaId}/${fileName}.512.jpg`;
    }
    return '';
}

function mangaDexLanguageCodes(languages: string[]): string[] {
    if (languages.includes('all')) return [];
    return [...new Set(languages.flatMap(language => language === 'es' ? ['es', 'es-la'] : [language]))];
}

function parseMangaStatus(statusStr?: string): YomiriManga['status'] {
    switch (statusStr) {
        case 'ongoing':
            return 'ONGOING';
        case 'completed':
            return 'COMPLETED';
        case 'cancelled':
            return 'CANCELLED';
        case 'hiatus':
            return 'ON_HIATUS';
        default:
            return 'UNKNOWN';
    }
}

/**
 * MangaDex Yomiri Extension
 * 
 * 100% direct browser execution via MangaDex CORS-friendly API.
 * No workers, no proxies, no backend servers.
 */
export class MangaDexSource implements YomiriExtension {
    public readonly id = 'mangadex';

    public readonly name = 'MangaDex';

    public readonly lang = 'es';

    public readonly version = '1.0.0';

    public readonly supportsLatest = true;

    public readonly isMultiLanguage = true;

    public readonly defaultLanguage = 'es';

    public readonly supportedLanguages: YomiriLanguageOption[] = [
        { code: 'es', name: 'Español (ES/LAT)', flag: '🇪🇸' },
        { code: 'en', name: 'Inglés (EN)', flag: '🇬🇧' },
        { code: 'pt-br', name: 'Portugués (BR)', flag: '🇧🇷' },
        { code: 'fr', name: 'Francés (FR)', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano (IT)', flag: '🇮🇹' },
        { code: 'de', name: 'Alemán (DE)', flag: '🇩🇪' },
        { code: 'ja', name: 'Japonés (JA)', flag: '🇯🇵' },
        { code: 'all', name: 'Todos los idiomas', flag: '🌐' },
    ];

    public readonly requiresExtension = false;

    public readonly requiredDomains = ['https://api.mangadex.org/*', 'https://uploads.mangadex.org/*'];

    public readonly iconUrl = "https://mangadex.org/favicon.ico";

    public parseUrl(url: string): { type: 'manga' | 'chapter'; mangaId: string; chapterId?: string } | null {
        try {
            const u = new URL(url);
            if (!u.hostname.includes('mangadex.org')) return null;

            const titleMatch = u.pathname.match(/\/title\/([0-9a-fA-F-]+)/);
            if (titleMatch) {
                return { type: 'manga', mangaId: titleMatch[1] };
            }

            const chapterMatch = u.pathname.match(/\/chapter\/([0-9a-fA-F-]+)/);
            if (chapterMatch) {
                return { type: 'chapter', mangaId: '', chapterId: chapterMatch[1] };
            }

            return null;
        } catch {
            return null;
        }
    }

    public async checkHealth(): Promise<{ ok: boolean; message: string }> {
        try {
            const res = await fetchSourceResponse(`${MANGADEX_API}/ping`);
            if (res.ok) {
                return { ok: true, message: 'MangaDex API reachable and responsive.' };
            }
            return { ok: false, message: `MangaDex responded with HTTP ${res.status}` };
        } catch (err: any) {
            return { ok: false, message: err.message || 'MangaDex unreachable' };
        }
    }

    public async getPopular(page: number = 1): Promise<YomiriMangasPage> {
        const offset = (page - 1) * 20;
        const url = await this.catalogUrl('followedCount', offset);

        const res = await fetchSourceResponse(url);
        if (!res.ok) {
            throw new Error(`MangaDex getPopular error: HTTP ${res.status}`);
        }
        const json = await res.json();
        const mangas = (json.data || []).map((m: any) => this.formatManga(m));

        return {
            mangas,
            hasNextPage: json.total > offset + 20,
        };
    }

    public async getLatest(page: number = 1): Promise<YomiriMangasPage> {
        const offset = (page - 1) * 20;
        const url = await this.catalogUrl('latestUploadedChapter', offset);

        const res = await fetchSourceResponse(url);
        if (!res.ok) {
            throw new Error(`MangaDex getLatest error: HTTP ${res.status}`);
        }
        const json = await res.json();
        const mangas = (json.data || []).map((m: any) => this.formatManga(m));

        return {
            mangas,
            hasNextPage: json.total > offset + 20,
        };
    }

    public async search(query: string, page: number = 1): Promise<YomiriMangasPage> {
        const clean = query.trim();
        if (!clean) {
            return this.getPopular(page);
        }

        const offset = (page - 1) * 20;
        const url = await this.catalogUrl('', offset, clean);

        const res = await fetchSourceResponse(url);
        if (!res.ok) {
            throw new Error(`MangaDex search error: HTTP ${res.status}`);
        }
        const json = await res.json();
        const mangas = (json.data || []).map((m: any) => this.formatManga(m));

        return {
            mangas,
            hasNextPage: json.total > offset + 20,
        };
    }

    public async getManga(mangaId: string): Promise<YomiriManga> {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(mangaId)) {
            throw new Error(`ID de manga inválido para MangaDex: "${mangaId}"`);
        }
        const url = `${MANGADEX_API}/manga/${mangaId}?includes[]=cover_art&includes[]=author&includes[]=artist`;

        const res = await fetchSourceResponse(url);
        if (!res.ok) {
            throw new Error(`MangaDex getManga error: HTTP ${res.status}`);
        }
        const json = await res.json();
        return this.formatManga(json.data);
    }

    public async getChapters(mangaId: string, options?: { language?: string; languages?: string[] }): Promise<YomiriChapter[]> {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(mangaId)) {
            throw new Error(`ID de manga inválido para MangaDex: "${mangaId}"`);
        }

        const activeLangs = await this.resolveLanguages(options);

        let chaptersData: any[] = [];

        const langQueryParams = mangaDexLanguageCodes(activeLangs).map(lang => `translatedLanguage[]=${encodeURIComponent(lang)}`).join('&');
        const languageQuery = langQueryParams ? `&${langQueryParams}` : '';

        try {
            const url = `${MANGADEX_API}/manga/${mangaId}/feed?limit=100&offset=0${languageQuery}&order[chapter]=desc&includeExternalUrl=0`;
            const res = await fetchSourceResponse(url);
            if (res.ok) {
                const j = await res.json();
                chaptersData = j.data || [];
            }
        } catch (err: any) {
            console.warn('Error fetching MangaDex chapters with language filter:', err);
        }

        const chapters = chaptersData.map((item: any) => {
            const attr = item.attributes || {};
            const chNum = parseFloat(attr.chapter || '0') || 0;
            const langCode = attr.translatedLanguage || 'es';
            const langTag = langCode === 'es-la' ? 'ES-LA' : langCode.toUpperCase();
            const langSuffix = ` [${langTag}]`;
            const chapterTitle = attr.title ? `: ${attr.title}` : '';
            const name = `Capítulo ${attr.chapter || '?'}${chapterTitle}${langSuffix}`;

            return {
                id: item.id,
                mangaId,
                sourceId: this.id,
                name,
                chapterNumber: chNum,
                language: langCode,
                uploadDate: attr.publishAt || attr.createdAt,
                scanlator: attr.translatedLanguage || 'MangaDex',
                pageCount: attr.pages || 0,
                isRead: false,
                isBookmarked: false,
                lastPageRead: 0,
            };
        });
        return [...new Map(chapters.map(chapter => [`${chapter.chapterNumber}:${chapter.language}`, chapter])).values()];
    }

    private async resolveLanguages(options?: { language?: string; languages?: string[] }): Promise<string[]> {
        if (options?.languages?.length) return options.languages;
        if (options?.language) return [options.language];
        try {
            const saved = await yomiriDatabase.getSetting<string[]>('source_languages_mangadex', ['es']);
            return saved?.length ? saved : ['es'];
        } catch { return ['es']; }
    }

    private async catalogUrl(order: string, offset: number, title?: string): Promise<string> {
        const languages = await this.resolveLanguages();
        const params = new URLSearchParams({ limit: '20', offset: String(offset), 'includes[]': 'cover_art', 'contentRating[]': 'safe' });
        params.append('contentRating[]', 'suggestive');
        if (order) params.set(`order[${order}]`, 'desc');
        if (title) params.set('title', title);
        for (const language of mangaDexLanguageCodes(languages)) params.append('availableTranslatedLanguage[]', language);
        return `${MANGADEX_API}/manga?${params.toString()}`;
    }

    public async getPages(chapterId: string): Promise<YomiriPage[]> {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(chapterId)) {
            throw new Error(`ID de capítulo inválido para MangaDex: "${chapterId}"`);
        }
        const url = `${MANGADEX_API}/at-home/server/${chapterId}`;

        const res = await fetchSourceResponse(url);
        if (!res.ok) {
            throw new Error(`MangaDex getPages error: HTTP ${res.status}`);
        }
        const json = await res.json();
        const { baseUrl } = json;
        const hash = json.chapter?.hash;

        // Use dataSaver for 4x faster loading and ultra-low bandwidth consumption
        const useSaver = Array.isArray(json.chapter?.dataSaver) && json.chapter.dataSaver.length > 0;
        const fileNames: string[] = useSaver ? json.chapter.dataSaver : (json.chapter?.data || []);
        const modeFolder = useSaver ? 'data-saver' : 'data';

        return fileNames.map((fileName, index) => ({
            index,
            imageUrl: `${baseUrl}/${modeFolder}/${hash}/${fileName}`,
            ...(useSaver && json.chapter?.data?.[index] ? { fallbackImageUrl: `${baseUrl}/data/${hash}/${json.chapter.data[index]}` } : {}),
        }));
    }

    private formatManga(item: any): YomiriManga {
        const attr = item.attributes || {};
        const rels = item.relationships || [];

        const authorRel = rels.find((r: any) => r.type === 'author');
        const artistRel = rels.find((r: any) => r.type === 'artist');

        const genres = (attr.tags || [])
            .filter((t: any) => t.attributes?.group === 'genre' || t.attributes?.group === 'theme')
            .map((t: any) => t.attributes?.name?.en)
            .filter(Boolean);

        return {
            id: item.id,
            sourceId: this.id,
            title: parseTitle(attr.title),
            description: parseDescription(attr.description),
            coverUrl: getCoverUrl(item.id, rels),
            author: authorRel?.attributes?.name,
            artist: artistRel?.attributes?.name,
            createdAt: attr.createdAt,
            sourceUpdatedAt: attr.updatedAt,
            status: parseMangaStatus(attr.status),
            genres,
            url: `https://mangadex.org/title/${item.id}`,
            inLibrary: false,
        };
    }
}

export const mangaDexSourceInstance = new MangaDexSource();


